import { ShoppingBag } from "lucide-react";
import { Button, Icon } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useSyncMerch } from "../../app/hooks/useMerchRollup.js";

/**
 * "Sync merch" — the Booking page's PAST-tab tool (ART-67).
 *
 * It backfills the per-show POS sales the automatic 6am sweep could not reach. That sweep offers a show only
 * while it is inside `commerce.pos_settlement_lookback_days` (seven, by default), so a band that connects
 * Shopify or Square after a run of dates — or rides out a POS outage longer than a week — has past shows
 * whose merch would otherwise never attach at all. Pressing this queues one attach per unattached past show.
 *
 * It cannot overwrite: the attach is once-per-show at the database, so this only ever FILLS a gap. That is
 * what makes it safe to press twice, and safe to press while the sweep is running against the same shows.
 *
 * **Its own component, and it self-gates.** `money:merch` is the token — everything the action produces is a
 * merch figure attributed to a show, the same reason the rollup carries it. Gating INSIDE rather than at the
 * call site keeps `BookingScreen` money-free by construction: the screen decides only WHICH TAB shows a tool,
 * never which seat may see money, which is the split the whole booking surface is built on.
 */
export function SyncMerchButton() {
  const canSeeMerchMoney = useCapability("money:merch");
  const sync = useSyncMerch();
  if (!canSeeMerchMoney) return null;

  return (
    <Button
      variant="ghost"
      size="xs"
      loading={sync.isPending}
      onClick={() => sync.mutate()}
    >
      <Icon icon={ShoppingBag} size={16} /> Sync merch
    </Button>
  );
}
